import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import AideIcon from '@mui/icons-material/HelpOutlineOutlined';
import DeplierIcon from '@mui/icons-material/ExpandMore';
import AdministrateurIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BibliothequeIcon from '@mui/icons-material/LocalLibraryOutlined';
import CoachIcon from '@mui/icons-material/SportsOutlined';
import AthleteIcon from '@mui/icons-material/DirectionsRunOutlined';

type Role = { cle: string; libelle: string; icone: React.ReactNode; texte: string };

// Les roles d'un club (cf ClubService cote serveur). Une personne peut en
// cumuler plusieurs : un coach est souvent aussi administrateur et bibliothecaire.
const ROLES: Role[] = [
  {
    cle: 'administrateur',
    libelle: 'Administrateur',
    icone: <AdministrateurIcon color="primary" />,
    texte: 'Gère le club : son nom, l\'invitation et le retrait des coachs, les rôles de chacun, et le coach de chaque athlète. '
      + 'Il ne voit pas l\'entraînement des athlètes qu\'il ne suit pas. Le club garde toujours au moins un administrateur. '
      + 'Un administrateur peut ne pas entraîner : il n\'a alors ni calendrier, ni journal, ni séances, '
      + 'et ne compte pas parmi les coachs du club.',
  },
  {
    cle: 'coach',
    libelle: 'Coach',
    icone: <CoachIcon color="primary" />,
    texte: 'Suit ses athlètes et établit leur calendrier. Il utilise la bibliothèque du club et peut en dupliquer les séances '
      + 'dans ses séances personnelles pour les adapter.',
  },
  {
    cle: 'bibliothecaire',
    libelle: 'Bibliothécaire',
    icone: <BibliothequeIcon color="primary" />,
    texte: 'Coach qui gère la bibliothèque du club : il y publie des séances, les modifie ou les retire. '
      + 'Sans bibliothécaire, la bibliothèque reste utilisable mais personne ne peut la modifier.',
  },
  {
    cle: 'athlete',
    libelle: 'Athlète',
    icone: <AthleteIcon color="primary" />,
    texte: 'Entre dans le club en acceptant l\'invitation d\'un coach, qui établit alors son calendrier. '
      + 'S\'il quitte son coach, il reste dans le club ; s\'il quitte le club, il quitte aussi son coach.',
  },
];

/** Aide repliable sur les roles du club ; les roles de l'utilisateur sont signales. */
export default function AideRoles({ mesRoles }: { mesRoles: string[] }) {
  return (
    <Accordion variant="outlined" disableGutters sx={{ mb: 3, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<DeplierIcon />} aria-controls="aide-roles" id="aide-roles-titre">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AideIcon color="action" />
          <Typography>Comprendre les rôles du club</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails id="aide-roles">
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {ROLES.map((r) => (
            <Box key={r.cle} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ pt: 0.25 }}>{r.icone}</Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography fontWeight={600}>{r.libelle}</Typography>
                  {mesRoles.includes(r.cle) && <Chip size="small" color="primary" variant="outlined" label="Toi" />}
                </Box>
                <Typography variant="body2" color="text.secondary">{r.texte}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Une personne peut cumuler plusieurs rôles. Les administrateurs les attribuent avec les boutons
          {' '}<AdministrateurIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> (administrateur),
          {' '}<CoachIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> (entraîne ou non) et
          {' '}<BibliothequeIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> (bibliothécaire) à côté de chaque membre.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
